import {
  DISPLAY_STAGES,
  NON_GAME_DISPLAY_STAGES,
  RUNNING_GAME_DISPLAY_STAGES,
} from "../shared/displayStages";

const staticTransition = {
  initial: { opacity: 1 },
  animate: { opacity: 1 },
  exit: { opacity: 1 },
  transition: { duration: 0 },
};

const immovableContainerTransition = staticTransition;

export const displayTransitionPresets = Object.freeze({
  static: staticTransition,
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.18, ease: "easeOut" },
  },
  questionEnter: immovableContainerTransition,
  questionSlide: immovableContainerTransition,
  answerReveal: immovableContainerTransition,
  factFade: immovableContainerTransition,
  leaderboardReveal: immovableContainerTransition,
  adBreakFade: immovableContainerTransition,
  roundIntroReveal: immovableContainerTransition,
  gameOverReveal: immovableContainerTransition,
});

export const displayTransitionCategories = Object.freeze({
  question: "questionEnter",
  reveal: "answerReveal",
  fact: "factFade",
  leaderboard: "leaderboardReveal",
  adBreak: "adBreakFade",
  roundIntro: "roundIntroReveal",
  completion: "gameOverReveal",
});

export const displayStageTransitionPolicy = Object.freeze({
  [DISPLAY_STAGES.ACTIVE_QUESTION]: "questionEnter",
  [DISPLAY_STAGES.SHOW_ANSWER]: "answerReveal",
  [DISPLAY_STAGES.FUN_FACT]: "factFade",
  [DISPLAY_STAGES.SHOW_QUESTION_RESULTS]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_ROUND_PARLOR_STANDINGS]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100]: "leaderboardReveal",
  [DISPLAY_STAGES.NEXT_QUESTION_INTRO]: "questionSlide",
  [DISPLAY_STAGES.AD_BREAK]: "adBreakFade",
  [DISPLAY_STAGES.ROUND_INTRO]: "roundIntroReveal",
  [DISPLAY_STAGES.GAME_OVER]: "gameOverReveal",
  question: "questionEnter",
  activeQuestion: "questionEnter",
  answerReveal: "answerReveal",
  fact: "factFade",
  funFact: "factFade",
  leaderboard: "leaderboardReveal",
  questionLeaderboard: "leaderboardReveal",
  gameLeaderboard: "leaderboardReveal",
  adBreak: "adBreakFade",
  roundTransition: "roundIntroReveal",
  gameOver: "gameOverReveal",
  postGame: "gameOverReveal",
});

export const liveDisplayStageTransitionPolicy = Object.freeze({
  [DISPLAY_STAGES.ACTIVE_QUESTION]: "questionEnter",
  [DISPLAY_STAGES.SHOW_ANSWER]: "answerReveal",
  [DISPLAY_STAGES.FUN_FACT]: "factFade",
  [DISPLAY_STAGES.SHOW_QUESTION_RESULTS]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_QUESTION_GLOBAL_TOP_100]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_ROUND_PARLOR_STANDINGS]: "leaderboardReveal",
  [DISPLAY_STAGES.SHOW_ROUND_GLOBAL_TOP_100]: "leaderboardReveal",
  [DISPLAY_STAGES.NEXT_QUESTION_INTRO]: "questionSlide",
  [DISPLAY_STAGES.AD_BREAK]: "adBreakFade",
  [DISPLAY_STAGES.ROUND_INTRO]: "roundIntroReveal",
  [DISPLAY_STAGES.GAME_OVER]: "gameOverReveal",
});

const runningGameDisplayStageSet = new Set(RUNNING_GAME_DISPLAY_STAGES);
const nonGameDisplayStageSet = new Set(NON_GAME_DISPLAY_STAGES);

export function isRunningGameDisplayTransitionStage(displayStage) {
  return runningGameDisplayStageSet.has(displayStage);
}

export function isNonGameDisplayStage(displayStage) {
  return nonGameDisplayStageSet.has(displayStage);
}

export function getLiveDisplayTransitionPreset(displayStage) {
  return liveDisplayStageTransitionPolicy[displayStage] || null;
}

// True for the stationary transition every live stage uses today, and that
// reduced motion falls back to: nothing moves on enter or exit.
export function isStaticDisplayTransition(transitionPreset) {
  return transitionPreset === staticTransition;
}

export function getDisplayTransitionPreset(
  presetName,
  { reducedMotion = false } = {}
) {
  if (presetName === "static") {
    return staticTransition;
  }

  if (!presetName || !displayTransitionPresets[presetName]) {
    return null;
  }

  if (reducedMotion) {
    return staticTransition;
  }

  return displayTransitionPresets[presetName];
}

export function getDisplayTransitionPresetForCategory(
  category,
  options
) {
  const presetName = displayTransitionCategories[category];
  return getDisplayTransitionPreset(presetName, options);
}

export function getDisplayTransitionPresetForStage(displayStage, options) {
  const presetName = displayStageTransitionPolicy[displayStage];
  return getDisplayTransitionPreset(presetName, options);
}
